import { useState, type MouseEvent } from "react";
import { useLingui } from "@lingui/react";
import { formatMonthYear } from "@/lib/format";

export interface SparkPoint {
  period: string;
  value: number;
}

interface MetricSparklineProps {
  /** The months the sparkline draws, oldest first. */
  points: readonly SparkPoint[];
  /** The value as the card prints it. */
  format: (value: number) => string;
  /** The point's label under the pointer; a month (`YYYY-MM`) by default. */
  formatPeriod?: (period: string) => string;
}

const WIDTH = 240;
const HEIGHT = 32;
const PAD = 2;

/**
 * The hub card's twelve months (#2175 B2) with a desktop affordance the phone
 * cannot offer: the pointer reads the month and the value under it. The readout
 * sits above the line, a guide line marks the month.
 */
export function MetricSparkline({ points, format, formatPeriod }: MetricSparklineProps) {
  const { i18n } = useLingui();
  const [hover, setHover] = useState<number | null>(null);
  if (points.length < 2) return null;

  const values = points.map((p) => p.value);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const span = max - min || 1;
  const x = (i: number) => PAD + (i / (points.length - 1)) * (WIDTH - PAD * 2);
  const y = (v: number) => PAD + (HEIGHT - PAD * 2) * (1 - (v - min) / span);
  const line = points.map((p, i) => `${x(i)},${y(p.value)}`).join(" ");
  const area = `${x(0)},${HEIGHT - PAD} ${line} ${x(points.length - 1)},${HEIGHT - PAD}`;

  const onMove = (e: MouseEvent<SVGSVGElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    const ratio = box.width > 0 ? (e.clientX - box.left) / box.width : 1;
    setHover(Math.min(points.length - 1, Math.max(0, Math.round(ratio * (points.length - 1)))));
  };
  const active = hover === null ? null : points[hover];

  return (
    <span className="relative block" data-testid="metric-sparkline" onMouseLeave={() => setHover(null)}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        className="block h-8 w-full text-[hsl(var(--chart-1))]"
        aria-hidden="true"
        onMouseMove={onMove}
      >
        <polygon points={area} fill="currentColor" fillOpacity={0.1} stroke="none" />
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        {hover !== null ? (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={0}
            y2={HEIGHT}
            className="stroke-foreground/40"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>
      {active ? (
        <span
          role="tooltip"
          className="pointer-events-none absolute -top-7 right-0 whitespace-nowrap rounded-md border bg-popover px-2 py-0.5 text-xs tabular-nums text-popover-foreground shadow-sm"
        >
          {i18n._({
            id: "analytics.metrics.sparkline.point",
            message: "{month}: {value}",
            values: {
              month: formatPeriod ? formatPeriod(active.period) : formatMonthYear(active.period, i18n.locale, "medium"),
              value: format(active.value),
            },
          })}
        </span>
      ) : null}
    </span>
  );
}
